import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import JobPanel from '../components/JobPanel';
import AgentToggle from '../components/AgentToggle';
import { IconSpinner, IconCheck, IconClose, IconWarning } from '../components/Icons';

const TABS = [
  { id: 'controllo', label: 'Controllo' },
  { id: 'cronologia', label: 'Cronologia Job' },
  { id: 'statistiche', label: 'Statistiche 456' },
];

export default function VerifichePage() {
  const [tab, setTab] = useState('controllo');
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [jobHistory, setJobHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const pollRef = useRef(null);
  const agentRef = useRef(null);

  useEffect(() => {
    init();
    agentRef.current = setInterval(pollAgent, 3000);
    return () => { clearInterval(pollRef.current); clearInterval(agentRef.current); };
    // eslint-disable-next-line
  }, []);

  async function pollAgent() {
    try {
      const r = await axios.get(`${API}/agent/status`);
      setPendingCount(r.data.pending ?? 0);
      setActiveJob(r.data.job && r.data.job.status === 'running' ? r.data.job : null);
    } catch {}
  }

  async function init() {
    setLoading(true);
    try {
      const [agentRes, histRes, vStatsRes] = await Promise.allSettled([
        axios.get(`${API}/agent/status`),
        axios.get(`${API}/jobs-history`),
        axios.get(`${API}/verify-stats`),
      ]);
      if (agentRes.status === 'fulfilled') {
        setPendingCount(agentRes.value.data.pending ?? 0);
        if (agentRes.value.data.job && agentRes.value.data.job.status === 'running') {
          setActiveJob(agentRes.value.data.job);
        }
      }
      if (histRes.status === 'fulfilled') setJobHistory(histRes.value.data?.items ?? []);
      if (vStatsRes.status === 'fulfilled') setStats(vStatsRes.value.data);
    } catch {} finally { setLoading(false); }
  }

  async function handleCancel() {
    if (!activeJob) return;
    try { await axios.post(`${API}/jobs/${activeJob.id}/cancel`); toast.success('Annullamento richiesto'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  }

  if (loading) {
    return (
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 20px', color: 'var(--text)' }}>Verifiche</h1>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <IconSpinner size={32} /><p style={{ marginTop: 12, color: 'var(--muted)' }}>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 20px', color: 'var(--text)' }}>Verifiche</h1>

      {/* Active job */}
      {activeJob && <JobPanel job={activeJob} onCancel={handleCancel} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--line)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: 'none', borderBottom: tab === t.id ? '2px solid var(--info)' : '2px solid transparent',
            color: tab === t.id ? 'var(--info)' : 'var(--muted)', background: 'none',
            marginBottom: -2, transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Controllo */}
      {tab === 'controllo' && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <AgentToggle />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>In attesa</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--warn)' }}>{pendingCount.toLocaleString('it-IT')}</div>
            </div>
          </div>
          <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--muted)' }}>
            Obiettivi e priorità si gestiscono dall'icona accanto all'interruttore agente nella barra a sinistra.
          </p>
        </div>
      )}

      {/* Tab: Cronologia */}
      {tab === 'cronologia' && (
        <div className="card">
          <h2>Cronologia Job</h2>
          {jobHistory.length === 0 ? (
            <p style={{ color: 'var(--faint)', fontSize: 13 }}>Nessun job registrato.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Stato</th><th>Avviato</th><th>Completato</th><th>Totale</th>
                    <th>TIM</th><th>NON TIM</th><th>Errori</th><th>Durata</th>
                  </tr>
                </thead>
                <tbody>
                  {jobHistory.map((j, i) => {
                    const statusIcon = j.status === 'completed' ? <IconCheck size={12} color="#059669" />
                      : j.status === 'cancelled' ? <IconClose size={12} color="#dc2626" />
                      : j.status === 'running' ? <IconSpinner size={12} />
                      : j.status === 'error' ? <IconWarning size={12} color="#dc2626" />
                      : null;
                    const durata = j.created_at && j.finished_at
                      ? formatDuration(new Date(j.finished_at) - new Date(j.created_at))
                      : j.status === 'running' ? 'In corso...' : '—';
                    return (
                      <tr key={j.id || i}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {statusIcon}
                            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{j.status}</span>
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {j.created_at ? new Date(j.created_at).toLocaleString('it-IT') : '—'}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {j.finished_at ? new Date(j.finished_at).toLocaleString('it-IT') : '—'}
                        </td>
                        <td style={{ fontWeight: 700 }}>{j.total ?? 0}</td>
                        <td style={{ color: 'var(--err)', fontWeight: 600 }}>{j.tim_count ?? 0}</td>
                        <td style={{ color: 'var(--ok)', fontWeight: 600 }}>{j.non_tim_count ?? 0}</td>
                        <td style={{ color: 'var(--muted)' }}>{j.errors ?? 0}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>{durata}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Statistiche */}
      {tab === 'statistiche' && stats && (
        <div>
          <div className="card">
            <h2>Analisi 456</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
              <StatCard label="Verificati" value={stats.totale_verificati} />
              <StatCard label="TIM" value={stats.tim} pct={stats.pct_tim} color="#ef4444" />
              <StatCard label="NON TIM" value={stats.non_tim} pct={stats.pct_non_tim} color="#22c55e" />
              <StatCard label="Errori" value={stats.errore} pct={stats.pct_errore} color="#64748b" />
              <StatCard label="In attesa" value={stats.pending} color="#f59e0b" />
              <StatCard label="Num. inattive" value={stats.numerazione_inattiva} color="#b45309" />
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
              <div style={{ background: '#f0f9ff', borderRadius: 8, padding: '12px 20px', flex: '1 1 200px' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ultime 24h</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--info)' }}>{stats.verifiche_24h?.toLocaleString('it-IT') ?? 0}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>~{stats.velocita_media_ora ?? 0}/ora</div>
              </div>
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '12px 20px', flex: '1 1 200px' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Disservizi 456</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: stats.totale_disservizi > 0 ? 'var(--err)' : 'var(--ok)' }}>
                  {stats.totale_disservizi ?? 0}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>registrati</div>
              </div>
            </div>

            {stats.totale_verificati > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Distribuzione risultati</div>
                <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${stats.pct_tim}%`, background: 'var(--err)', transition: 'width 0.3s' }} title={`TIM: ${stats.pct_tim}%`} />
                  <div style={{ width: `${stats.pct_non_tim}%`, background: 'var(--ok)', transition: 'width 0.3s' }} title={`NON TIM: ${stats.pct_non_tim}%`} />
                  <div style={{ width: `${stats.pct_errore}%`, background: 'var(--faint)', transition: 'width 0.3s' }} title={`Errori: ${stats.pct_errore}%`} />
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11 }}>
                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--err)', marginRight: 4 }} />TIM {stats.pct_tim}%</span>
                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--ok)', marginRight: 4 }} />NON TIM {stats.pct_non_tim}%</span>
                  <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--faint)', marginRight: 4 }} />Errori {stats.pct_errore}%</span>
                </div>
              </div>
            )}
          </div>

          {stats.province && stats.province.length > 0 && (
            <div className="card">
              <h2>Analisi per Provincia</h2>
              <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Provincia</th><th>Verificati</th><th>TIM</th><th>NON TIM</th><th>Errori</th><th>% NON TIM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.province.map((p, i) => {
                      const pctNonTim = p.totale > 0 ? Math.round(p.non_tim / p.totale * 100) : 0;
                      return (
                        <tr key={p._id || i}>
                          <td style={{ fontWeight: 600 }}>{p._id}</td>
                          <td>{p.totale}</td>
                          <td style={{ color: 'var(--err)' }}>{p.tim}</td>
                          <td style={{ color: 'var(--ok)' }}>{p.non_tim}</td>
                          <td style={{ color: 'var(--muted)' }}>{p.errore}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden', maxWidth: 80 }}>
                                <div style={{ width: `${pctNonTim}%`, height: '100%', background: 'var(--ok)', borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 30 }}>{pctNonTim}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Helper components ── */
function StatCard({ label, value, pct, color = 'var(--text)' }) {
  return (
    <div style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 8, borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value?.toLocaleString('it-IT') ?? 0}</div>
      {pct !== undefined && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{pct}%</div>}
    </div>
  );
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min ${sec % 60}s`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}min`;
}
